import React, { useState, useEffect } from 'react';
import { appRepository, AppState, ParentSettings } from '../../core/storage/repository';
import { speechProvider } from '../../core/speech/speechProvider';
import {
  isParentGateCoolingDown,
  isParentSessionExpired,
  MAX_PARENT_PIN_ATTEMPTS,
  PARENT_GATE_COOLDOWN_MS,
  verifyParentPin,
} from '../../core/parent/parentGate';
import { BackIcon, LockIcon, SettingsIcon, SpeakerIcon, SummaryIcon, TipIcon } from '../../components/icons/ReadingIcons';

interface ParentDashboardProps {
  onBack: () => void;
}

export const ParentDashboard: React.FC<ParentDashboardProps> = ({ onBack }) => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [unlockedAt, setUnlockedAt] = useState<number | null>(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [clock, setClock] = useState(() => Date.now());
  const [holdProgress, setHoldProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);

  const [state, setState] = useState<AppState>(appRepository.getState());
  const [settings, setSettings] = useState<ParentSettings>(state.settings);
  const [childNameInput, setChildNameInput] = useState(state.profile.nickname);
  const [childLevelInput, setChildLevelInput] = useState(state.profile.learningLevel);
  const [nameSavedMessage, setNameSavedMessage] = useState('');

  useEffect(() => {
    const unsub = appRepository.subscribe(s => {
      setState(s);
      setSettings(s.settings);
      setChildNameInput(s.profile.nickname);
      setChildLevelInput(s.profile.learningLevel);
    });
    return unsub;
  }, []);

  // Parent Gate: 3-second hold listener
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isHolding) {
      timer = setInterval(() => {
        setHoldProgress(prev => {
          if (prev >= 100) {
            unlockParentSession();
            setIsHolding(false);
            return 100;
          }
          return Math.min(100, prev + 100 / 30);
        });
      }, 100);
    } else {
      setHoldProgress(0);
    }
    return () => clearInterval(timer);
  }, [isHolding]);

  useEffect(() => {
    if (!isUnlocked && !isParentGateCoolingDown(cooldownUntil, clock)) return;
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [clock, cooldownUntil, isUnlocked]);

  useEffect(() => {
    if (isUnlocked && unlockedAt !== null && isParentSessionExpired(unlockedAt, clock)) {
      setIsUnlocked(false);
      setUnlockedAt(null);
      setPinInput('');
    }
  }, [clock, isUnlocked, unlockedAt]);

  const unlockParentSession = () => {
    setIsUnlocked(true);
    setUnlockedAt(Date.now());
    setFailedAttempts(0);
    setCooldownUntil(0);
    setPinError(false);
  };

  const handleLock = () => {
    setIsUnlocked(false);
    setUnlockedAt(null);
    setPinInput('');
  };

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isParentGateCoolingDown(cooldownUntil)) {
      setPinError(true);
      return;
    }
    if (verifyParentPin(pinInput, settings.parentPin)) {
      unlockParentSession();
      return;
    }

    const nextAttempts = failedAttempts + 1;
    setPinError(true);
    setPinInput('');
    if (nextAttempts >= MAX_PARENT_PIN_ATTEMPTS) {
      setFailedAttempts(0);
      setCooldownUntil(Date.now() + PARENT_GATE_COOLDOWN_MS);
    } else {
      setFailedAttempts(nextAttempts);
    }
  };

  const handleUpdateSettings = (updates: Partial<ParentSettings>) => {
    appRepository.updateSettings(updates);
  };

  const handleUpdateChildName = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nickname = childNameInput.trim().replace(/\s+/g, ' ');
    if (!nickname) {
      setNameSavedMessage('Nama anak belum diisi.');
      return;
    }

    appRepository.updateProfile({
      nickname: nickname.slice(0, 30),
      learningLevel: childLevelInput,
    });
    setNameSavedMessage('Nama anak sudah disimpan.');
  };

  const handleResetProgress = async () => {
    if (window.confirm('Mulai ulang riwayat belajar anak? Profil nama anak tetap tersimpan.')) {
      await appRepository.resetProgress();
      alert('Progres belajar berhasil diatur ulang.');
    }
  };

  const handleExportData = async () => {
    const json = await appRepository.exportData();
    const blobUrl = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `ayo-baca-${state.profile.nickname.toLowerCase()}-data.json`;
    link.click();
    URL.revokeObjectURL(blobUrl);
  };

  const handleDeleteAllData = async () => {
    if (window.confirm('Hapus semua data belajar dari perangkat ini? Tindakan ini tidak dapat dibatalkan.')) {
      await appRepository.deleteAllData();
      alert('Semua data belajar sudah dihapus dari perangkat.');
    }
  };

  // If not unlocked, display Parent Gate
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-[#FFFDF8] flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-6 md:p-8 shadow-2xl border-4 border-amber-200 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center text-3xl mx-auto mb-3">
            <LockIcon className="w-9 h-9" />
          </div>
          <h2 className="text-2xl font-display font-black text-[#243047]">
            Pintu Khusus Orang Tua
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Area ini khusus untuk Ayah & Bunda melihat catatan belajar dan pengaturan.
          </p>

          {/* Option 1: Hold Button */}
          <div className="mt-6 p-4 bg-amber-50 rounded-2xl border border-amber-200">
            <div className="text-xs font-bold text-amber-900 mb-2">
              Cara Cepat: Tekan dan tahan tombol selama 3 detik
            </div>
            <button
              onPointerDown={() => setIsHolding(true)}
              onPointerUp={() => setIsHolding(false)}
              onPointerLeave={() => setIsHolding(false)}
              className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-bold rounded-xl shadow relative overflow-hidden select-none"
            >
              <span className="relative z-10">
                {isHolding ? `Tahan sebentar (${Math.round(holdProgress)}%)...` : 'Tekan & Tahan di Sini'}
              </span>
              <div
                className="absolute left-0 top-0 bottom-0 bg-amber-600 transition-all duration-100"
                style={{ width: `${holdProgress}%` }}
              />
            </button>
          </div>

          <div className="my-4 text-xs text-gray-400 font-bold">— ATAU MASUKKAN PIN —</div>

          {/* Option 2: PIN Form */}
          <form onSubmit={handlePinSubmit} className="flex gap-2 justify-center">
            <input
              type="password"
              maxLength={4}
              placeholder="PIN 4 digit"
              aria-label="PIN orang tua"
              inputMode="numeric"
              pattern="[0-9]{4}"
              value={pinInput}
              onChange={e => {
                setPinInput(e.target.value);
                setPinError(false);
              }}
              className="w-32 bg-gray-50 border-2 border-gray-300 rounded-xl px-3 py-2 text-center text-lg font-bold tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-[#4F7CF7] text-white font-bold rounded-xl text-sm shadow hover:bg-blue-600"
            >
              Buka
            </button>
          </form>
          {pinError && (
            <p className="text-xs text-red-500 font-bold mt-2">
              {isParentGateCoolingDown(cooldownUntil, clock)
                ? `Terlalu banyak percobaan. Coba lagi dalam ${Math.ceil((cooldownUntil - clock) / 1000)} detik.`
                : 'PIN belum benar. Coba lagi dengan tenang.'}
            </p>
          )}

          <button
            onClick={onBack}
            className="mt-6 text-sm text-gray-500 hover:text-gray-700 font-bold"
          >
            Kembali ke Halaman Anak
          </button>
        </div>
      </div>
    );
  }

  // Unlocked Parent Dashboard View
  const { profile, progress, discoveredWords } = state;

  return (
    <div className="flex flex-col min-h-screen bg-[#F8FAFC] pb-12 text-[#243047]">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold px-3 py-2 rounded-xl text-xs md:text-sm"
          >
            <BackIcon className="w-5 h-5" />
            <span>Mode Anak</span>
          </button>
          <h1 className="text-lg md:text-xl font-bold font-display text-gray-900">
            Dasbor Orang Tua
          </h1>
        </div>

        <div className="text-xs text-gray-500 font-medium">
          Profil: <strong className="text-gray-900">{profile.nickname}</strong> ({profile.learningLevel})
        </div>
        <button
          onClick={handleLock}
          className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold px-3 py-2 rounded-xl text-xs md:text-sm"
        >
          <LockIcon className="w-5 h-5" />
          <span>Kunci</span>
        </button>
      </div>

      <div className="max-w-4xl mx-auto w-full px-4 pt-6 flex-1 flex flex-col gap-6">
        {/* Child Profile */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-xs">
          <h2 className="font-bold text-base text-gray-900 mb-1">Profil Anak</h2>
          <p className="text-xs text-gray-500 mb-4">Nama dan tingkat belajar ini akan tampil di aplikasi.</p>
          <form onSubmit={handleUpdateChildName} className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <label className="flex-1">
              <span className="block text-xs font-bold text-gray-600 mb-1">Nama panggilan</span>
              <input
                value={childNameInput}
                maxLength={30}
                onChange={event => {
                  setChildNameInput(event.target.value);
                  setNameSavedMessage('');
                }}
                className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                aria-label="Nama panggilan anak"
              />
            </label>
            <label className="flex-1">
              <span className="block text-xs font-bold text-gray-600 mb-1">Tingkat belajar</span>
              <select
                value={childLevelInput}
                onChange={event => {
                  setChildLevelInput(event.target.value as typeof childLevelInput);
                  setNameSavedMessage('');
                }}
                className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                aria-label="Tingkat belajar anak"
              >
                <option value="PAUD">PAUD</option>
                <option value="TK A">TK A</option>
                <option value="TK B">TK B</option>
              </select>
            </label>
            <button
              type="submit"
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-sm"
            >
              Simpan Nama
            </button>
          </form>
          {nameSavedMessage && (
            <p className="text-xs text-emerald-700 font-semibold mt-2" role="status">
              {nameSavedMessage}
            </p>
          )}
        </div>

        {/* Child Progress Overview Cards */}
        <div>
          <h2 className="text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
            <><SummaryIcon className="w-7 h-7" /> Ringkasan Aktivitas Belajar {profile.nickname}</>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
              <div className="text-xs font-semibold text-gray-500">Hari Belajar</div>
              <div className="text-2xl md:text-3xl font-black text-blue-600 mt-1">
                {progress.learningDays} <span className="text-xs font-normal text-gray-400">hari</span>
              </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
              <div className="text-xs font-semibold text-gray-500">Total Durasi</div>
              <div className="text-2xl md:text-3xl font-black text-emerald-600 mt-1">
                {progress.learningMinutes} <span className="text-xs font-normal text-gray-400">menit</span>
              </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
              <div className="text-xs font-semibold text-gray-500">Huruf Dikenal</div>
              <div className="text-2xl md:text-3xl font-black text-amber-600 mt-1">
                {progress.lettersFamiliar.length} <span className="text-xs font-normal text-gray-400">/ 26</span>
              </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
              <div className="text-xs font-semibold text-gray-500">Kata Ditemukan</div>
              <div className="text-2xl md:text-3xl font-black text-teal-600 mt-1">
                {discoveredWords.length} <span className="text-xs font-normal text-gray-400">kata</span>
              </div>
            </div>
          </div>
        </div>

        {/* Factual, Supportive Practice Recommendation */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
          <h3 className="font-bold text-blue-950 text-sm md:text-base flex items-center gap-2">
            <><TipIcon className="w-7 h-7" /> Catatan Pendampingan Belajar</>
          </h3>
          <p className="text-xs md:text-sm text-blue-900 mt-1.5 leading-relaxed">
            {profile.nickname} sudah mengenal baik huruf{' '}
            <strong>{progress.lettersFamiliar.slice(0, 5).join(', ')}</strong>.
            Suku kata yang saat ini sedang dipelajari:{' '}
            <strong>{progress.syllablesFamiliar.slice(0, 4).join(', ')}</strong>.
            Ajak anak membaca rambu atau kemasan saat di luar rumah untuk memperkuat pengenalan kata nyata!
          </p>
        </div>

        {/* Alphabet Familiarity Matrix */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs">
          <h3 className="font-bold text-sm text-gray-800 mb-3">
            Peta Pengenalan 26 Huruf:
          </h3>
          <div className="flex flex-wrap gap-2">
            {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => {
              const isLearned = progress.lettersFamiliar.includes(letter);
              return (
                <span
                  key={letter}
                  className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm ${
                    isLearned
                      ? 'bg-blue-600 text-white shadow-xs'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {letter}
                </span>
              );
            })}
          </div>
        </div>

        {/* Settings & Privacy Configuration */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-xs">
          <h3 className="font-bold text-base text-gray-900 mb-4 flex items-center gap-2">
            <><SettingsIcon className="w-7 h-7" /> Pengaturan Suara & Privasi</>
          </h3>

          <div className="space-y-4">
            {/* Speech Rate Toggle */}
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div>
                <div className="font-semibold text-sm">Kecepatan Suara Membaca</div>
                <div className="text-xs text-gray-500">Membantu artikulasi anak saat menyimak suku kata</div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleUpdateSettings({ speechRate: 'normal' })}
                  className={`px-3 py-1.5 rounded-xl font-bold text-xs ${
                    settings.speechRate === 'normal'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  Normal
                </button>
                <button
                  onClick={() => handleUpdateSettings({ speechRate: 'slow' })}
                  className={`px-3 py-1.5 rounded-xl font-bold text-xs ${
                    settings.speechRate === 'slow'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  Pelan
                </button>
              </div>
            </div>

            {/* Privacy: Photo Retention */}
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div>
                <div className="font-semibold text-sm">Privasi Foto Kamera</div>
                <div className="text-xs text-gray-500">Foto hanya diproses di perangkat lokal, tidak diunggah ke server luar</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.retainDiscoveryImages}
                  onChange={e => handleUpdateSettings({ retainDiscoveryImages: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Speech Test */}
            <div className="flex items-center justify-between pt-1">
              <div>
                <div className="font-semibold text-sm">Uji Suara Perangkat</div>
                <div className="text-xs text-gray-500">Pastikan pengeras suara HP aktif</div>
              </div>
              <button
                onClick={() => speechProvider.speak('Halo Ayah dan Bunda, suara membaca sudah siap!', { rate: settings.speechRate === 'slow' ? 0.75 : 0.9 })}
                className="px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold rounded-xl text-xs flex items-center gap-1.5"
              >
                <SpeakerIcon className="w-5 h-5" />
                <span>Uji Suara</span>
              </button>
            </div>
          </div>
        </div>

        {/* Destructive Actions */}
        <div className="bg-red-50/70 border border-red-200 rounded-2xl p-5">
          <h4 className="font-bold text-sm text-red-900 mb-1">
            Area Berisiko
          </h4>
          <p className="text-xs text-red-700 mb-3">
            Mengatur ulang riwayat latihan dan kata yang tersimpan pada perangkat ini.
          </p>
          <button
            onClick={handleResetProgress}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl shadow-xs transition"
          >
            Mulai Ulang Riwayat Belajar
          </button>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={handleExportData}
              className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 font-bold text-xs rounded-xl border border-gray-300 shadow-xs transition"
            >
              Ekspor Data JSON
            </button>
            <button
              onClick={handleDeleteAllData}
              className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white font-bold text-xs rounded-xl shadow-xs transition"
            >
              Hapus Semua Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
